import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Calendar, Check, MapPin, Plus, Search, ShoppingBag } from "lucide-react";
import { brl, catLabel, fmtDate, img } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { Eyebrow, MaskLines } from "@/components/site/Motion";
import { Lightbox } from "@/components/gallery/Lightbox";
import { CheckoutDialog } from "@/components/gallery/CheckoutDialog";

const loadCart = (id) => {
  try {
    return new Set(JSON.parse(localStorage.getItem(`cart:${id}`) || "[]"));
  } catch (e) {
    return new Set();
  }
};

export default function EventGallery() {
  const { slug } = useParams();
  const { data: ev, loading, error } = useFetch(`/events/${slug}`);
  const { data: settings } = useFetch("/settings/public", {});
  const [selected, setSelected] = useState(new Set());
  const [code, setCode] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const [checkout, setCheckout] = useState(false);

  useEffect(() => {
    if (ev?.id) setSelected(loadCart(ev.id));
  }, [ev?.id]);

  const photos = useMemo(() => {
    const list = Array.isArray(ev?.photos) ? ev.photos : [];
    return code ? list.filter((p) => p.code.toLowerCase().includes(code.toLowerCase())) : list;
  }, [ev, code]);

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
    localStorage.setItem(`cart:${ev.id}`, JSON.stringify([...next]));
  };
  const clear = () => {
    setSelected(new Set());
    localStorage.removeItem(`cart:${ev?.id}`);
  };

  if (loading) return <main className="min-h-screen pt-40 text-center text-haze">Carregando galeria…</main>;
  if (error || !ev) return (
    <main className="min-h-screen pt-40 text-center" data-testid="gallery-not-found">
      <p className="font-display text-3xl font-bold">Evento não encontrado.</p>
      <Link to="/eventos" className="mt-4 inline-block text-flash">Ver todos os eventos</Link>
    </main>
  );

  const total = selected.size * (ev.price_per_photo_cents ?? 0);
  return (
    <main data-testid="event-gallery-page">
      <section className="grain relative overflow-hidden border-b border-white/10 pt-[120px]">
        {ev.cover_photo_id && <img src={img("preview", ev.cover_photo_id)} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />}
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/80 to-ink/40" />
        <div className="relative mx-auto max-w-[1400px] px-5 pb-12 pt-10 sm:px-8">
          <Eyebrow>{catLabel(ev.category)} · {ev.photo_count} fotos</Eyebrow>
          <h1 className="mt-5 font-display text-4xl font-extrabold leading-[0.95] tracking-tight sm:text-7xl" data-testid="gallery-title"><MaskLines lines={[ev.title]} /></h1>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-haze">
            {ev.date && <span className="inline-flex items-center gap-2"><Calendar className="h-4 w-4" />{fmtDate(ev.date)}</span>}
            {ev.location && <span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4" />{ev.location}</span>}
            {ev.photographer && <Link to={`/fotografos/${ev.photographer.id}`} className="hover:text-flash" data-testid="gallery-photographer-link">por {ev.photographer.name}</Link>}
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <span className="rounded-full bg-flash px-5 py-2.5 font-semibold text-ink" data-testid="gallery-price">{brl(ev.price_per_photo_cents)} por foto</span>
            <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-4 py-2.5">
              <Search className="h-4 w-4 text-haze" />
              <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Buscar pelo código" className="w-40 bg-transparent text-sm outline-none" data-testid="gallery-code-search" />
            </div>
          </div>
          {ev.description && <p className="mt-6 max-w-2xl text-sm text-haze">{ev.description}</p>}
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 py-12 pb-40 sm:px-8">
        <p className="mb-6 text-sm text-haze">Toque na foto para ampliar. Use <Plus className="inline h-4 w-4" /> para selecionar as suas.</p>
        <div className="columns-2 gap-3 md:columns-3 lg:columns-4" data-testid="gallery-grid">
          {photos.map((p, i) => {
            const isSel = selected.has(p.id);
            return (
              <div key={p.id} className={`group relative mb-3 break-inside-avoid overflow-hidden rounded-2xl border-2 transition-colors duration-300 ${isSel ? "border-flash" : "border-transparent"}`}>
                <button onClick={() => setLightbox(photos.indexOf(p))} className="block w-full" data-testid={`gallery-photo-${p.code}`}>
                  <img src={img("thumb", p.id)} alt={p.code} loading="lazy" draggable={false} onContextMenu={(e) => e.preventDefault()}
                    style={{ aspectRatio: p.width && p.height ? `${p.width}/${p.height}` : "4/3" }}
                    className="w-full bg-ink-2 object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                </button>
                <span className="pointer-events-none absolute bottom-2 left-2 rounded-md bg-ink/75 px-2 py-0.5 font-mono text-[11px] backdrop-blur">{p.code}</span>
                <button onClick={() => toggle(p.id)} aria-label="Selecionar" data-testid={`gallery-select-${p.code}`}
                  className={`absolute right-2 top-2 grid h-10 w-10 place-items-center rounded-full transition-[background-color,transform] duration-300 hover:scale-110 ${isSel ? "bg-flash text-ink" : "bg-ink/70 text-white backdrop-blur"}`}>
                  {isSel ? <Check className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
                </button>
              </div>
            );
          })}
        </div>
        {!photos.length && <p className="py-20 text-center text-haze" data-testid="gallery-empty">Nenhuma foto encontrada.</p>}
      </section>

      <AnimatePresence>
        {selected.size > 0 && (
          <motion.div initial={{ y: 120 }} animate={{ y: 0 }} exit={{ y: 120 }} transition={{ type: "spring", stiffness: 260, damping: 28 }}
            className="fixed inset-x-3 bottom-3 z-40 mx-auto flex max-w-2xl items-center justify-between gap-3 rounded-full border border-white/15 bg-ink/85 p-2 pl-5 backdrop-blur-xl sm:inset-x-6" data-testid="cart-bar">
            <div className="flex items-center gap-3">
              <ShoppingBag className="h-5 w-5 text-flash" />
              <span className="text-sm"><b data-testid="cart-count">{selected.size}</b> foto(s) · <b className="text-flash" data-testid="cart-total">{brl(total)}</b></span>
              <button onClick={clear} className="hidden text-xs text-haze underline sm:inline" data-testid="cart-clear-btn">limpar</button>
            </div>
            <button onClick={() => setCheckout(true)} className="rounded-full bg-flash px-6 py-3 text-sm font-semibold text-ink hover:bg-flash-2" data-testid="cart-checkout-btn">Comprar fotos</button>
          </motion.div>
        )}
      </AnimatePresence>

      {lightbox !== null && <Lightbox photos={photos} index={lightbox} setIndex={setLightbox} selected={selected} toggle={toggle} />}
      <CheckoutDialog open={checkout} onOpenChange={setCheckout} event={ev} photoIds={[...selected]} mpEnabled={!!settings?.mp_enabled} onDone={clear} />
    </main>
  );
}
